import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import type {
  CardQuality,
  InGameCurrency,
  RewardConfigRarity,
} from '@sorare/core/src/__generated__/globalTypes';
import {
  DivisionChange,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Box } from '@sorare/core/src/atoms/icons/Box';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import {
  FixtureOverviewBezel,
  InlineFixtureOverviewBezel,
} from '@sorare/core/src/atoms/ui/bezels/FixtureOverviewBezel';
import { Variant } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { groupBy, sortBy } from '@sorare/core/src/lib/arrays';
import { getRarityRGBColor } from '@sorare/core/src/lib/colors';
import {
  fantasy,
  glossary,
  marketCredits,
} from '@sorare/core/src/lib/glossary';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';
import { qualityNames } from '@sorare/core/src/lib/players';
import {
  isEnergyCurrency,
  isXPCurrency,
  mergeRewardsConfig,
} from '@sorare/core/src/lib/rewards';
import {
  RANKED_SCARCITY,
  inGameCurrencyToRarity,
} from '@sorare/core/src/lib/scarcity';

import { ClickableAnyRewardConfig } from '#src/components/rewards/ClickableAnyRewardConfig/index.tsx';

import type {
  RewardConfigsOverview_anyRewardConfigInterface,
  RewardConfigsOverview_rarityRewardConfigs,
  RewardConfigsOverview_so5Lineup,
} from './__generated__/index.graphql.ts';

const Block = styled.div`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background: var(--c-nd-50);
`;
const BoostedList = styled(Vertical).attrs({
  gap: 2,
})`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background: var(--c-nd-50);
`;
const WrapHorizontal = styled(Horizontal)`
  flex-wrap: wrap;
`;
const DivisionChanges = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--quadruple-unit);
`;
const Progress = styled.div`
  width: 100%;
  height: var(--unit);
  border-radius: var(--unit);
  background: var(--bg-color);
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: calc(100% - var(--progress));
    background: var(--progress-color);
    border-radius: var(--double-unit);
  }
`;

type Props = {
  rewardConfigs: RewardConfigsOverview_anyRewardConfigInterface[];
  boostedRewardConfigsByRarity: RewardConfigsOverview_rarityRewardConfigs[];
  prepend?: ReactNode;
  so5Lineups: RewardConfigsOverview_so5Lineup[];
  onlyRewards?: boolean;
};
export const RewardConfigsOverview = ({
  rewardConfigs,
  boostedRewardConfigsByRarity,
  prepend,
  so5Lineups,
  onlyRewards,
}: Props) => {
  const conversionCreditRewardConfigs = rewardConfigs.filter(
    isTypeFn('ConversionCreditRewardConfig')
  );
  const boxRewardConfigs = rewardConfigs.filter(
    isTypeFn('ProbabilisticBundleRewardConfig')
  );
  const sortedBoxRewardConfigs = boxRewardConfigs.sort((a, b) => {
    const configA = a.probabilisticBundleConfig;
    const configB = b.probabilisticBundleConfig;

    const scarcityCompare =
      RANKED_SCARCITY[configB.rarity] - RANKED_SCARCITY[configA.rarity];
    const starCompare = (configB.stars || 0) - (configA.stars || 0);

    return scarcityCompare || starCompare;
  });

  const cardRewardConfigs = rewardConfigs.filter(isTypeFn('CardRewardConfig'));
  const cardCountsPerRarityPerQuality = cardRewardConfigs.reduce(
    (acc, rewardConfig) => {
      const { configRarity, quality } = rewardConfig;
      if (!configRarity || !quality) {
        return acc;
      }

      return {
        ...acc,
        [configRarity]: {
          ...acc[configRarity],
          count: (acc[configRarity]?.count || 0) + 1,
          [quality]: (acc[configRarity]?.[quality] || 0) + 1,
        },
      };
    },
    {} as Record<
      RewardConfigRarity,
      { count: number } & Record<CardQuality, number>
    >
  );

  const shardRewardConfigs = rewardConfigs.filter(
    isTypeFn('CardShardRewardConfig')
  );
  const shardCountsPerRarity = shardRewardConfigs.reduce(
    (acc, rewardConfig) => {
      const { configRarity, quantity } = rewardConfig;

      return {
        ...acc,
        [configRarity]: (acc[configRarity] || 0) + quantity,
      };
    },
    {} as Record<RewardConfigRarity, number>
  );

  const xpRewardConfigs = rewardConfigs
    .filter(isTypeFn('InGameCurrencyRewardConfig'))
    .filter(config => isXPCurrency(config.currency));

  const xpCountsPerCurrency = xpRewardConfigs.reduce(
    (acc, rewardConfig) => {
      const { amount, currency } = rewardConfig;

      return {
        ...acc,
        [currency]: (acc[currency] || 0) + amount,
      };
    },
    {} as Record<InGameCurrency, number>
  );
  const energyRewardConfigs = rewardConfigs
    .filter(isTypeFn('InGameCurrencyRewardConfig'))
    .filter(config => isEnergyCurrency(config.currency));

  const energyCountsPerCurrency = energyRewardConfigs.reduce(
    (acc, rewardConfig) => {
      const { amount, currency } = rewardConfig;

      return {
        ...acc,
        [currency]: (acc[currency] || 0) + amount,
      };
    },
    {} as Record<InGameCurrency, number>
  );

  const otherRewardConfigs = rewardConfigs.filter(
    rewardConfig =>
      !isType(rewardConfig, 'CardRewardConfig') &&
      !isType(rewardConfig, 'ProbabilisticBundleRewardConfig') &&
      !isType(rewardConfig, 'MonetaryRewardConfig') &&
      !isType(rewardConfig, 'InGameCurrencyRewardConfig') &&
      !isType(rewardConfig, 'CardShardRewardConfig')
  );

  const eligibleDivisionChanges = (so5Lineups || [])
    .flatMap(so5Lineup => so5Lineup.so5Rankings.at(0)?.eligibleDivisionChange)
    .filter(Boolean);

  const divisionChangesPerChange = groupBy(
    eligibleDivisionChanges,
    change => change
  );

  const fancyRewardsPresent = otherRewardConfigs.length > 0;

  const orderedBoostedRewardConfigsByRarity = sortBy(
    ({ rarity }) => RANKED_SCARCITY[(rarity || 'mix') as Rarity | 'mix'],
    [...(boostedRewardConfigsByRarity || [])]
  );

  return (
    <Vertical gap={3}>
      {prepend}
      {boxRewardConfigs.length > 0 && (
        <Vertical gap={1.5}>
          <LabelM bold color="var(--c-nd-600)">
            <FormattedMessage {...fantasy.boxes} />
          </LabelM>
          <WrapHorizontal>
            {sortedBoxRewardConfigs.map((rewardConfig, index) => (
              <FixtureOverviewBezel
                // eslint-disable-next-line react/no-array-index-key
                key={`box-${rewardConfig.probabilisticBundleConfig.rarity}-${index}`}
                rgbColor={getRarityRGBColor(
                  rewardConfig.probabilisticBundleConfig.rarity
                )}
              >
                <ClickableAnyRewardConfig
                  key={rewardConfig.id}
                  anyRewardConfig={rewardConfig}
                />
              </FixtureOverviewBezel>
            ))}
          </WrapHorizontal>
        </Vertical>
      )}
      {conversionCreditRewardConfigs.length > 0 && (
        <Vertical gap={1.5}>
          <LabelM bold color="var(--c-nd-600)">
            <FormattedMessage {...marketCredits.marketCredits} />
          </LabelM>
          <InlineFixtureOverviewBezel rgbColor="195, 243, 203">
            <WrapHorizontal>
              {mergeRewardsConfig(conversionCreditRewardConfigs).map(
                rewardConfig => (
                  <ClickableAnyRewardConfig
                    key={rewardConfig.id}
                    anyRewardConfig={rewardConfig}
                    variant={Variant.TEXT}
                    textVariantSize="lg"
                  />
                )
              )}
            </WrapHorizontal>
          </InlineFixtureOverviewBezel>
        </Vertical>
      )}
      {cardRewardConfigs.length > 0 && (
        <Vertical gap={1.5}>
          <LabelM bold color="var(--c-nd-600)">
            <FormattedMessage {...glossary.cards} />
          </LabelM>
          <Vertical gap={2}>
            {Object.entries(cardCountsPerRarityPerQuality).map(
              ([rarity, { count, ...qualities }]) => (
                <FixtureOverviewBezel
                  key={`card-${rarity}`}
                  rgbColor={getRarityRGBColor(rarity as Rarity)}
                >
                  <WrapHorizontal>
                    <ScarcityIcon rarity={rarity as Rarity} size={4} />
                    <BrandL>{count}</BrandL>
                    {sortBy(entry => entry[0], Object.entries(qualities)).map(
                      ([quality, qualityCount]) => (
                        <Chip key={quality}>
                          <Horizontal gap={0.5}>
                            <LabelM bold color="var(--c-white)">
                              <FormattedNumber value={qualityCount} />
                            </LabelM>
                            <LabelM color="var(--c-nd-600)">
                              {qualityNames[quality as CardQuality]}
                            </LabelM>
                          </Horizontal>
                        </Chip>
                      )
                    )}
                  </WrapHorizontal>
                </FixtureOverviewBezel>
              )
            )}
          </Vertical>
        </Vertical>
      )}
      {shardRewardConfigs.length > 0 && (
        <Vertical gap={1.5}>
          <LabelM bold color="var(--c-nd-600)">
            <FormattedMessage {...glossary.essence} />
          </LabelM>
          <WrapHorizontal>
            {Object.entries(shardCountsPerRarity).map(([rarity, count]) => {
              return (
                <InlineFixtureOverviewBezel
                  key={`shard-${rarity}`}
                  rgbColor={getRarityRGBColor(rarity as Rarity)}
                >
                  <ClickableAnyRewardConfig
                    anyRewardConfig={{
                      __typename: 'CardShardRewardConfig',
                      id: '',
                      configRarity: rarity as RewardConfigRarity,
                      quantity: count,
                      cardSet: null,
                    }}
                    variant={Variant.TEXT}
                    textVariantSize="lg"
                  />
                </InlineFixtureOverviewBezel>
              );
            })}
          </WrapHorizontal>
        </Vertical>
      )}
      {energyRewardConfigs.length > 0 && (
        <Vertical gap={1.5}>
          <LabelM bold color="var(--c-nd-600)">
            <FormattedMessage
              id="FixtureOverview.energy"
              defaultMessage="Energy"
            />
          </LabelM>
          <WrapHorizontal>
            {Object.entries(energyCountsPerCurrency).map(
              ([currency, count]) => {
                return (
                  <InlineFixtureOverviewBezel
                    key={currency}
                    rgbColor={getRarityRGBColor(
                      inGameCurrencyToRarity[currency as InGameCurrency] ||
                        Rarity.common
                    )}
                  >
                    <ClickableAnyRewardConfig
                      anyRewardConfig={{
                        __typename: 'InGameCurrencyRewardConfig',
                        id: '',
                        currency: currency as InGameCurrency,
                        amount: count,
                      }}
                      variant={Variant.TEXT}
                      textVariantSize="lg"
                    />
                  </InlineFixtureOverviewBezel>
                );
              }
            )}
          </WrapHorizontal>
        </Vertical>
      )}
      {xpRewardConfigs.length > 0 && (
        <Vertical gap={1.5}>
          <LabelM bold color="var(--c-nd-600)">
            <FormattedMessage {...glossary.xp} />
          </LabelM>
          <WrapHorizontal>
            {Object.entries(xpCountsPerCurrency).map(([currency, count]) => {
              return (
                <InlineFixtureOverviewBezel
                  key={currency}
                  rgbColor={getRarityRGBColor(
                    inGameCurrencyToRarity[currency as InGameCurrency] ||
                      Rarity.common
                  )}
                >
                  <ClickableAnyRewardConfig
                    anyRewardConfig={{
                      __typename: 'InGameCurrencyRewardConfig',
                      id: '',
                      currency: currency as InGameCurrency,
                      amount: count,
                    }}
                    variant={Variant.TEXT}
                    textVariantSize="lg"
                  />
                </InlineFixtureOverviewBezel>
              );
            })}
          </WrapHorizontal>
        </Vertical>
      )}
      {orderedBoostedRewardConfigsByRarity.flatMap(
        ({ rewardConfigs: boostedRewardConfigs }) => boostedRewardConfigs
      ).length > 0 && (
        <Vertical gap={1.5}>
          <Horizontal gap={0.5}>
            <Box gradient="green" />
            <LabelM bold color="var(--c-nd-600)">
              <FormattedMessage
                id="FixtureOverview.rewardBoosts"
                defaultMessage="Reward Boosts"
              />
            </LabelM>
          </Horizontal>
          <Vertical gap={2}>
            {orderedBoostedRewardConfigsByRarity.map(
              ({ rarity, rewardConfigs: boostedRewardConfigs }, index) => {
                if (boostedRewardConfigs.length === 0) {
                  return null;
                }
                return (
                  <InlineFixtureOverviewBezel
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    rgbColor="61, 220, 132"
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 'var(--unit)',
                        left: 'var(--double-unit)',
                      }}
                    >
                      <ScarcityIcon rarity={rarity || 'mix'} />
                    </div>
                    <Horizontal
                      center
                      style={
                        {
                          '--text-gradient-color':
                            'linear-gradient(323deg, #f1ff00 -3.1%, #3ddc84 52.7%)',
                        } as CSSProperties
                      }
                      grow
                    >
                      {mergeRewardsConfig(boostedRewardConfigs).map(
                        rewardConfig => (
                          <ClickableAnyRewardConfig
                            key={rewardConfig.id}
                            anyRewardConfig={rewardConfig}
                            customText={({ rewardText }) => (
                              <span className="text-gradient-color">
                                +{rewardText}
                              </span>
                            )}
                            variant={Variant.TEXT}
                            textVariantSize="lg"
                          />
                        )
                      )}
                    </Horizontal>
                  </InlineFixtureOverviewBezel>
                );
              }
            )}
          </Vertical>
        </Vertical>
      )}
      {onlyRewards ? (
        <>
          {!!fancyRewardsPresent && (
            <Vertical gap={1.5}>
              <LabelM bold color="var(--c-nd-600)">
                <FormattedMessage
                  id="FixtureOverview.specials"
                  defaultMessage="Specials"
                />
              </LabelM>
              <Block>
                <WrapHorizontal>
                  {otherRewardConfigs.map(rewardConfig => {
                    return (
                      <ClickableAnyRewardConfig
                        key={rewardConfig.id}
                        anyRewardConfig={rewardConfig}
                      />
                    );
                  })}
                </WrapHorizontal>
              </Block>
            </Vertical>
          )}
        </>
      ) : (
        <>
          {eligibleDivisionChanges.length > 0 && (
            <Vertical gap={1.5}>
              <LabelM bold color="var(--c-nd-600)">
                <FormattedMessage
                  id="FixtureOverview.overallPerformance"
                  defaultMessage="Overall Performance"
                />
              </LabelM>
              <BoostedList>
                <DivisionChanges>
                  <Vertical center>
                    <LabelS>
                      <FormattedMessage {...fantasy.promotion} />
                    </LabelS>
                    <Progress
                      style={
                        {
                          '--bg-color': 'rgba(var(--c-rgb-onBase-success), .2)',
                          '--progress-color': 'var(--c-onBase-success)',
                          '--progress': `${
                            ((divisionChangesPerChange[DivisionChange.PROMOTED]
                              ?.length || 0) /
                              eligibleDivisionChanges.length) *
                            100
                          }%`,
                        } as CSSProperties
                      }
                    />
                    <LabelS color="var(--c-onBase-success)">
                      <FormattedNumber
                        value={
                          divisionChangesPerChange[DivisionChange.PROMOTED]
                            ?.length || 0
                        }
                      />
                    </LabelS>
                  </Vertical>
                  <Vertical center>
                    <LabelS>
                      <FormattedMessage
                        id="FixtureOverview.safety"
                        defaultMessage="Safety"
                      />
                    </LabelS>
                    <Progress
                      style={
                        {
                          '--bg-color': 'rgba(var(--c-rgb-white), .1)',
                          '--progress-color': 'var(--c-white)',
                          '--progress': `${
                            ((divisionChangesPerChange[DivisionChange.STABLE]
                              ?.length || 0) /
                              eligibleDivisionChanges.length) *
                            100
                          }%`,
                        } as CSSProperties
                      }
                    />
                    <LabelS>
                      <FormattedNumber
                        value={
                          divisionChangesPerChange[DivisionChange.STABLE]
                            ?.length || 0
                        }
                      />
                    </LabelS>
                  </Vertical>
                  <Vertical center>
                    <LabelS>
                      <FormattedMessage {...fantasy.relegation} />
                    </LabelS>
                    <Progress
                      style={
                        {
                          '--bg-color': 'rgba(255, 78, 34, .2)',
                          '--progress-color': 'var(--c-rewards-orange)',
                          '--progress': `${
                            ((divisionChangesPerChange[DivisionChange.RELEGATED]
                              ?.length || 0) /
                              eligibleDivisionChanges.length) *
                            100
                          }%`,
                        } as CSSProperties
                      }
                    />
                    <LabelS color="var(--c-rewards-orange)">
                      <FormattedNumber
                        value={
                          divisionChangesPerChange[DivisionChange.RELEGATED]
                            ?.length || 0
                        }
                      />
                    </LabelS>
                  </Vertical>
                </DivisionChanges>
              </BoostedList>
            </Vertical>
          )}
        </>
      )}
    </Vertical>
  );
};

RewardConfigsOverview.fragments = {
  so5Lineup: gql`
    fragment RewardConfigsOverview_so5Lineup on So5Lineup {
      id
      so5Leaderboard {
        slug
      }
      so5Rankings {
        id
        eligibleDivisionChange
      }
    }
  ` as TypedDocumentNode<RewardConfigsOverview_so5Lineup>,
  rarityRewardConfigs: gql`
    fragment RewardConfigsOverview_rarityRewardConfigs on RarityRewardConfigs {
      rarity
      rewardConfigs {
        id
        ... on MonetaryRewardConfig {
          id
        }
        ... on ProbabilisticBundleRewardConfig {
          id
          probabilisticBundleConfig {
            id
            rarity
            stars
          }
        }
        ... on CardShardRewardConfig {
          id
          quantity
          configRarity
        }
        ... on InGameCurrencyRewardConfig {
          id
          amount
          currency
        }
        ...mergeRewardsConfig_anyRewardConfigInterface
        ...ClickableAnyRewardConfig_anyRewardConfigInterface
      }
    }
    ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
    ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<RewardConfigsOverview_rarityRewardConfigs>,
  anyRewardConfigInterface: gql`
    fragment RewardConfigsOverview_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ... on MonetaryRewardConfig {
        id
      }
      ... on ProbabilisticBundleRewardConfig {
        id
        probabilisticBundleConfig {
          id
          rarity
          stars
        }
      }
      ... on CardShardRewardConfig {
        id
        quantity
        configRarity
      }
      ... on InGameCurrencyRewardConfig {
        id
        amount
        currency
      }
      ...ClickableAnyRewardConfig_anyRewardConfigInterface
    }
    ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<RewardConfigsOverview_anyRewardConfigInterface>,
};
